import axios from "axios";
import { AcceptPersonnelDtoType } from "./PersonnelApi";

const SERVICES_URL = process.env.SERVICES_URL;
const API_URL = `${SERVICES_URL}/roster`;

export type ExemptPersonnelDTO = {
    personnelId?: number;
    comment: string;
};

export const getRoster = (): Promise<any> =>
    axios.get(`${API_URL}`).then(response => response.data);

export const getAllRosterStatusCount = (personnelCountRequestDTO: {
    personnelTypeStr: string;
    clubIds: number[];
    seasonId?: number;
    eventId?: number;
    bgcScore?: string;
}): Promise<any> =>
    axios.post(`${API_URL}/count/status`, personnelCountRequestDTO).then(response => response.data);

export const certifyRoster = (): Promise<any> =>
    axios.post(`${API_URL}/certifyRoster`).then(response => response.data);

export const checkForCertification = (): Promise<any> =>
    axios.get(`${API_URL}/checkForCertification`).then(response => response.data);

export const exemptRoster = (
    rosterId: number,
    exemptPersonnelDTO: ExemptPersonnelDTO,
): Promise<any> =>
    axios.post(`${API_URL}/${rosterId}/exempt`, exemptPersonnelDTO).then(response => response.data);

export const acceptRoster = (
    rosterId: number,
    acceptPersonnelDTO: AcceptPersonnelDtoType,
): Promise<any> =>
    axios.post(`${API_URL}/${rosterId}/accept`, acceptPersonnelDTO).then(response => response.data);

export type AcceptAndSendQuestionnaireDTO = {
    personnelId: number;
};

export const acceptAndSendQuestionnaireRoster = (
    rosterId: number,
    acceptAndSendQuestionnaireDTO: AcceptAndSendQuestionnaireDTO,
): Promise<any> =>
    axios
        .post(`${API_URL}/${rosterId}/acceptAndSendQuestionnaire`, acceptAndSendQuestionnaireDTO)
        .then(response => response.data);

export const getEventMenuOptions = (): Promise<any> =>
    axios.get(`${API_URL}/eventsByStatus`).then(response => response.data);

export const moveStatus = (personnelId: number, statusChangeDTO: any): Promise<any> =>
    axios
        .put(`${API_URL}/${personnelId}/changeStatus`, statusChangeDTO)
        .then(response => response.data);

export const markCompleteRoster = (rosterId: number, markCompleteDTO: any): Promise<any> =>
    axios
        .post(`${API_URL}/${rosterId}/markComplete`, markCompleteDTO)
        .then(response => response.data);

export const markCompleteNonSterling = (
    rosterId: number,
    markCompleteNonSterlingDto: any,
): Promise<any> =>
    axios
        .post(`${API_URL}/${rosterId}/markCompleteNonSterling`, markCompleteNonSterlingDto)
        .then(response => response.data);

export const bulkAccept = (bulkAcceptPersonnelDTO: any): Promise<any> =>
    axios.post(`${API_URL}/bulk/accept`, bulkAcceptPersonnelDTO).then(response => response.data);

export const bulkAcceptAndSendQuestionnaire = (bulkDto: {
    acceptAndSendQuestionnaireList: AcceptAndSendQuestionnaireDTO[];
}): Promise<any> =>
    axios
        .post(`${API_URL}/bulk/acceptAndSendQuestionnaire`, bulkDto)
        .then(response => response.data);

export const bulkExempt = (bulkExemptPersonnelDTO: any): Promise<any> =>
    axios.post(`${API_URL}/bulk/exempt`, bulkExemptPersonnelDTO).then(response => response.data);

export const getPersonnelActivityFeed = (rosterId: number): Promise<any> =>
    axios.get(`${API_URL}/personnelActivityFeed/${rosterId}`).then(response => response.data);

export const bulkRolloverInProgress = (bulkPersonnelDTO: any): Promise<any> =>
    axios
        .post(`${API_URL}/bulk/rolloverInProgress`, bulkPersonnelDTO)
        .then(response => response.data);

export const activateRosterStatus = (rosterStatusId: number): Promise<any> =>
    axios.post(`${API_URL}/status/${rosterStatusId}/activate`).then(response => response.data);

export const inactivateRosterStatus = (rosterStatusId: number): Promise<any> =>
    axios.post(`${API_URL}/status/${rosterStatusId}/inactivate`).then(response => response.data);

export type NeedsReviewRestoreRow = {
    rosterStatusId: number;
    date?: string;
    status?: string;
    description?: string;
    user?: string;
    comment?: string;
    country?: string;
    active?: string;
    visible?: string;
};

export type NeedsReviewMoveTarget = {
    status: string;
    valid: boolean;
    errorMessage?: string;
};

export type NeedsReviewRestoreContext = {
    priorRosterStatusIds: number[];
    availableRows: NeedsReviewRestoreRow[];
    moveTargets: NeedsReviewMoveTarget[];
    needsReviewComment?: string;
    triggerComments: string[];
};

export const getNeedsReviewRestoreContext = (
    personnelId: number,
): Promise<NeedsReviewRestoreContext> =>
    axios
        .get(`${SERVICES_URL}/manual-fix/needs-review-restore-context/${personnelId}`)
        .then(response => response.data);

export type RestoreFromNeedsReviewResponse = {
    outcome: string;
    message: string;
};

export const restoreFromNeedsReview = (
    personnelId: number,
    rosterStatusIds: number[],
): Promise<RestoreFromNeedsReviewResponse> =>
    axios
        .post(`${SERVICES_URL}/manual-fix/restore-from-needs-review/${personnelId}`, {
            rosterStatusIds,
        })
        .then(response => response.data);

export const moveToStatusFromNeedsReview = (
    personnelId: number,
    targetStatus: string,
): Promise<RestoreFromNeedsReviewResponse> =>
    axios
        .post(`${SERVICES_URL}/manual-fix/move-from-needs-review/${personnelId}`, {
            targetStatus,
        })
        .then(response => response.data);

export type BulkRestoreWorkflowsResponse = {
    restored: number[];
    failed: { personnelId: number; message: string }[];
    sentToSos: number[];
};

export const bulkRestoreWorkflows = (
    personnelIds: number[],
): Promise<BulkRestoreWorkflowsResponse> =>
    axios
        .post(`${SERVICES_URL}/manual-fix/bulk-restore-workflows`, { personnelIds })
        .then(response => response.data);

export type NeedsReviewReason = {
    id: number;
    code: string;
    description: string;
};

export const getNeedsReviewReasons = (): Promise<NeedsReviewReason[]> =>
    axios.get(`${SERVICES_URL}/manual-fix/needs-review/reasons`).then(response => response.data);

export type NeedsReviewBulkContextRow = {
    personnelId: number;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    affiliationAbbrev?: string;
    needsReviewComment?: string;
    triggerComments: string[];
    validMoveTargets: string[];
    priorRosterStatusIds: number[];
};

export type NeedsReviewBulkContextResponse = {
    rows: NeedsReviewBulkContextRow[];
};

export const getNeedsReviewBulkContext = (
    personnelIds: number[],
): Promise<NeedsReviewBulkContextResponse> =>
    axios
        .post(`${SERVICES_URL}/manual-fix/needs-review-bulk-context`, { personnelIds })
        .then(response => response.data);

export type NeedsReviewResolution = {
    personnelId: number;
    action: "RESTORE" | "MOVE_TO_STATUS";
    targetStatus?: string;
};

export type BulkResolvePersonDetail = {
    personnelId: number;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    affiliationAbbrev?: string;
    action: "RESTORE" | "MOVE_TO_STATUS";
    targetStatus?: string;
    priorRosterStatusIds?: number[];
    errorMessage?: string;
};

export type BulkResolveNeedsReviewResponse = {
    restored: BulkResolvePersonDetail[];
    moved: BulkResolvePersonDetail[];
    failed: BulkResolvePersonDetail[];
    sentToSos: BulkResolvePersonDetail[];
};

export const bulkResolveNeedsReview = (
    resolutions: NeedsReviewResolution[],
): Promise<BulkResolveNeedsReviewResponse> =>
    axios
        .post(`${SERVICES_URL}/manual-fix/bulk-resolve-needs-review`, { resolutions })
        .then(response => response.data);

export type SosBulkContextRow = {
    personnelId: number;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    affiliationAbbrev?: string;
    sosComment?: string;
    sentToSosComment?: string;
};

export type SosBulkContextResponse = {
    rows: SosBulkContextRow[];
};

export const getSosBulkContext = (personnelIds: number[]): Promise<SosBulkContextResponse> =>
    axios
        .post(`${SERVICES_URL}/manual-fix/sos-bulk-context`, { personnelIds })
        .then(response => response.data);

export type SosResolution = {
    personnelId: number;
    targetStatus: string;
};

export const bulkResolveSos = (
    resolutions: SosResolution[],
): Promise<BulkResolveNeedsReviewResponse> =>
    axios
        .post(`${SERVICES_URL}/manual-fix/bulk-resolve-sos`, { resolutions })
        .then(response => response.data);
